/**
 * Centralised help content for all modules of the app.
 * Each entry is consumed by <PageHelpButton moduleKey="xxx" /> and rendered
 * via <HelpInfoDialog />.
 *
 * Add new modules here. To avoid heavy imports, icons are referenced by name
 * (string) and resolved at render time inside PageHelpButton.
 */
const HELP = {
  dashboard: {
    title: '¿Qué es el Dashboard?',
    icon: 'LayoutDashboard',
    intro: 'Es el resumen de tu negocio en una pantalla. Te muestra lo importante de un vistazo: cuánto has facturado este mes, qué tienes pendiente de cobrar, qué gastos has tenido, próximos vencimientos y alertas que necesitan tu atención.',
    sections: [
      {
        icon: 'TrendingUp', iconColor: '#FF6B4A', title: 'Métricas principales',
        body: [
          '<strong>Facturado este mes</strong>: total de facturas emitidas',
          '<strong>Cobrado / Por cobrar</strong>: dinero recibido y aún pendiente',
          '<strong>Gastos</strong>: salidas registradas en el periodo',
          '<strong>Beneficio estimado</strong>: ingresos - gastos antes de impuestos',
        ],
      },
      {
        icon: 'AlertTriangle', iconColor: '#F59E0B', title: 'Alertas y avisos',
        body: 'Te avisa de cosas que requieren acción: facturas vencidas, suscripciones impagadas, vencimientos fiscales próximos, stock bajo, etc.',
      },
      {
        icon: 'Calendar', iconColor: '#7C3AED', title: 'Próximos eventos',
        body: 'Vencimientos de facturas, fechas de impuestos, recordatorios del calendario. Todo lo que pasa en los próximos 7-15 días.',
      },
    ],
  },

  invoices: {
    title: '¿Qué son las Facturas?',
    icon: 'FileText',
    intro: 'Las facturas son los documentos oficiales con los que cobras a tus clientes. Aquí las creas, las envías, llevas su estado y generas el PDF. Cumplen con la normativa española (numeración correlativa, IVA desglosado, datos fiscales, Verifactu cuando corresponde).',
    sections: [
      {
        icon: 'Plus', iconColor: '#FF6B4A', title: 'Cómo crear una',
        body: [
          'Pulsa "Nueva Factura" arriba a la derecha',
          'Elige cliente, fecha y conceptos (líneas con cantidad × precio × IVA)',
          'Puedes <strong>añadir servicios y artículos del catálogo</strong> en 1 clic',
          'Guardas como borrador o la marcas como enviada directamente',
        ],
      },
      {
        icon: 'CheckCircle2', iconColor: '#10B981', title: 'Estados',
        body: [
          '<strong>Borrador</strong>: aún no enviada, puedes editarla',
          '<strong>Enviada</strong>: ya en manos del cliente, esperando pago',
          '<strong>Pagada</strong>: cobrada (manual o automático vía banca PSD2)',
          '<strong>Vencida</strong>: pasó la fecha y sigue impagada',
          '<strong>Anulada</strong>: cancelada con motivo (queda registrada)',
        ],
      },
      {
        icon: 'Sparkles', iconColor: '#7C3AED', title: 'Acciones útiles',
        body: [
          '<strong>Descargar PDF</strong> con el diseño elegido en el banner superior',
          '<strong>Enviar por email</strong> al cliente con un clic',
          '<strong>Crear rectificativa</strong> si necesitas corregir una factura emitida',
          '<strong>Duplicar</strong> para crear una similar a otro cliente',
        ],
      },
    ],
  },

  quotes: {
    title: '¿Qué son los Presupuestos?',
    icon: 'ClipboardList',
    intro: 'Son ofertas que mandas al cliente <strong>antes de hacer el trabajo</strong>. Le dices cuánto le va a costar, qué incluye y cuándo caduca la oferta. Cuando el cliente acepta, lo conviertes en factura con 1 clic.',
    sections: [
      {
        icon: 'CheckCircle2', iconColor: '#10B981', title: 'Estados',
        body: [
          '<strong>Borrador</strong>: aún no enviado',
          '<strong>Enviado</strong>: cliente lo tiene, esperando respuesta',
          '<strong>Aceptado</strong>: cliente dijo "sí, adelante"',
          '<strong>Rechazado</strong>: cliente declinó',
          '<strong>Caducado</strong>: pasó la fecha de validez',
        ],
      },
      {
        icon: 'Sparkles', iconColor: '#FF6B4A', title: 'Convertir a factura',
        body: 'Cuando el cliente acepta, abre el presupuesto y pulsa "Convertir a factura". Se crea una factura con todos los conceptos, fecha de emisión hoy y vencimiento según tus condiciones.',
      },
      {
        icon: 'Calendar', iconColor: '#7C3AED', title: 'Diferencia clave con factura',
        body: 'Un presupuesto NO contabiliza ingresos ni IVA. Es solo una propuesta. Hasta que no lo conviertas a factura, no aparece en tus modelos fiscales ni en el cash flow real.',
      },
    ],
  },

  proformas: {
    title: '¿Qué son las Facturas Proforma?',
    icon: 'Layers',
    intro: 'Una factura proforma es un <strong>documento informativo no fiscal</strong> que envías al cliente antes de la operación: detalla precio, conceptos e IVA esperado, pero <strong>no contabiliza ingresos ni IVA</strong> ni se reporta a la AEAT. En cuanto el cliente confirma, la conviertes en factura real con 1 clic.',
    sections: [
      {
        icon: 'CheckCircle2', iconColor: '#10B981', title: 'Cuándo usarla',
        body: [
          'El cliente <strong>pide presupuesto formal con IVA</strong> antes de pagar (típico en pedidos internacionales y aduanas)',
          'Necesitas que el cliente <strong>pague por adelantado</strong> contra un documento detallado',
          'Quieres mostrar un <strong>desglose con IVA</strong> sin generar aún factura fiscal',
        ],
      },
      {
        icon: 'Sparkles', iconColor: '#FF6B4A', title: 'Convertir a factura',
        body: 'Cuando el cliente confirma, abre la proforma y pulsa <strong>"Convertir a factura"</strong>. Se crea una factura en borrador con todos los conceptos y la proforma queda marcada como "Convertida" (no se podrá borrar para mantener trazabilidad).',
      },
      {
        icon: 'AlertTriangle', iconColor: '#F59E0B', title: 'Importante',
        body: 'La proforma <strong>NO es válida fiscalmente</strong>. Tiene su propia serie (PRO{año}-NNNNN) y no aparece en modelos fiscales hasta que la conviertas en factura.',
      },
    ],
  },

  'delivery-notes': {
    title: '¿Qué son los Albaranes?',
    icon: 'Truck',
    intro: 'Son documentos que certifican la <strong>entrega de mercancía o servicio</strong>, pero <strong>sin valor fiscal</strong>. Típico en sector mayorista, almacén, distribución: entregas el material hoy con un albarán y a final de mes facturas todos juntos.',
    sections: [
      {
        icon: 'FileText', iconColor: '#FF6B4A', title: 'Cuándo usar albarán vs factura',
        body: [
          'Sector <strong>almacén/distribución</strong>: entregas frecuentes, factura mensual',
          'Cuando el cliente <strong>solo necesita justificante de recepción</strong>',
          'Antes de tener fijado el precio final',
          'Para llevar control interno de salidas de mercancía',
        ],
      },
      {
        icon: 'CheckCircle2', iconColor: '#10B981', title: 'Convertir albaranes en factura',
        body: 'Al final del mes (o cuando quieras) seleccionas varios albaranes del mismo cliente y los <strong>conviertes en una sola factura</strong>. Ahorras tiempo y el cliente recibe una sola factura por todas las entregas.',
      },
      {
        icon: 'AlertTriangle', iconColor: '#F59E0B', title: 'Importante',
        body: 'El albarán <strong>NO genera IVA ni contabilidad fiscal</strong>. Es solo un documento de entrega. La factura que lo facture sí tiene impacto fiscal.',
      },
    ],
  },

  contracts: {
    title: '¿Qué son los Contratos?',
    icon: 'FileSignature',
    intro: 'Documentos legales que firmas con tus clientes (servicios, mantenimiento, NDA, contrato laboral, etc.). Puedes <strong>generarlos con IA</strong> a partir de una breve descripción y enviarlos al portal del cliente para firma.',
    sections: [
      {
        icon: 'Sparkles', iconColor: '#7C3AED', title: 'Generación con IA',
        body: 'Le dices al asistente "necesito un contrato de mantenimiento web mensual con cláusula de exclusividad y aviso de 30 días" y te redacta un borrador profesional en segundos. Lo revisas, editas y lo mandas.',
      },
      {
        icon: 'FileText', iconColor: '#FF6B4A', title: 'Plantillas comunes',
        body: [
          'Contrato de servicios profesionales',
          'Contrato de prestación de servicios continuados',
          'Acuerdo de confidencialidad (NDA)',
          'Términos y condiciones',
          'Contrato de arrendamiento',
        ],
      },
      {
        icon: 'CheckCircle2', iconColor: '#10B981', title: 'Firma y archivo',
        body: 'El contrato se envía al portal del cliente. Una vez firmado queda archivado con timestamp y disponible para descargar como PDF en cualquier momento.',
      },
    ],
  },

  clients: {
    title: '¿Qué es el módulo Clientes?',
    icon: 'Users',
    intro: 'Tu base de datos comercial. Aquí guardas a quién facturas: razón social, CIF/NIF, dirección, contacto, IBAN y configuraciones específicas (plantilla de factura por defecto, tarifa, etc.).',
    sections: [
      {
        icon: 'Plus', iconColor: '#FF6B4A', title: 'Cómo añadir clientes',
        body: [
          '<strong>Uno a uno</strong>: botón "Nuevo cliente"',
          '<strong>Masivamente desde Excel</strong>: "Importar Excel" arriba',
          'Auto-completado de datos por <strong>CIF/NIF</strong> en formularios futuros',
        ],
      },
      {
        icon: 'Sparkles', iconColor: '#7C3AED', title: 'Funciones avanzadas',
        body: [
          '<strong>Portal del cliente</strong>: tu cliente puede ver sus facturas en su área privada',
          '<strong>Plantilla de factura por cliente</strong>: cada cliente puede tener su diseño PDF',
          '<strong>Rentabilidad</strong>: ves cuánto te aporta cada cliente vs cuánto te cuesta',
          '<strong>Suscripciones</strong>: facturación recurrente automática',
        ],
      },
    ],
  },

  providers: {
    title: '¿Qué son los Proveedores?',
    icon: 'Package',
    intro: 'Las empresas a las que TÚ pagas (luz, alquiler, materiales, software, asesoría, etc.). Los guardas para asociarlos automáticamente a tus gastos y poder hacer análisis de "cuánto le pago al mes a XX".',
    sections: [
      {
        icon: 'Sparkles', iconColor: '#FF6B4A', title: 'Por qué guardarlos',
        body: [
          'Al subir un ticket/factura de gasto, el OCR lo asocia <strong>automáticamente</strong>',
          'Ves cuánto gastas con cada proveedor al mes/año',
          'Detectas duplicidades (mismo proveedor con dos nombres)',
          'Útiles para el modelo 347 (operaciones >3.005,06 € anuales)',
        ],
      },
    ],
  },

  services: {
    title: '¿Qué son los Servicios?',
    icon: 'Briefcase',
    intro: 'Tu <strong>catálogo de servicios</strong>: trabajos repetitivos con precio fijo o tarifa por hora. Mantenimiento mensual, hora de consultoría, sesión de fisio, clase, etc.',
    sections: [
      {
        icon: 'Plus', iconColor: '#FF6B4A', title: 'Por qué usarlo',
        body: [
          'Al crear factura/presupuesto, los añades <strong>en 1 clic</strong>',
          'Mantienes precios consistentes entre clientes',
          'Cambios de precio se aplican fácil a futuras facturas',
          'Análisis de qué servicio es más rentable',
        ],
      },
      {
        icon: 'Sparkles', iconColor: '#7C3AED', title: 'Diferencia con Artículos',
        body: '<strong>Servicios</strong> = trabajo, horas, intangible (sin stock). <strong>Artículos</strong> = productos físicos con SKU/código de barras/stock.',
      },
    ],
  },

  articles: {
    title: '¿Qué son los Artículos?',
    icon: 'Boxes',
    intro: 'Tu <strong>catálogo de productos físicos</strong>: cosas que vendes con referencia, código de barras, precio y opcionalmente <strong>control de stock</strong>.',
    sections: [
      {
        icon: 'Boxes', iconColor: '#7C3AED', title: 'Campos principales',
        body: [
          '<strong>SKU/Referencia</strong>: código interno único',
          '<strong>Código de barras</strong>: EAN13 escaneable',
          '<strong>Precio + IVA</strong>: lo que cobras',
          '<strong>Unidad</strong>: unidad, kg, caja, m, l, pack...',
          '<strong>Stock + Stock mínimo</strong>: control opcional con alertas',
        ],
      },
      {
        icon: 'Sparkles', iconColor: '#FF6B4A', title: 'Stock automático en facturas',
        body: 'Si activas control de stock, al facturar un artículo se <strong>resta automáticamente</strong>. Si anulas la factura, el stock vuelve. Se registra cada movimiento.',
      },
      {
        icon: 'AlertTriangle', iconColor: '#F59E0B', title: 'Alertas de stock bajo',
        body: 'Define un stock mínimo. Cuando un artículo baja por debajo, aparece marcado en rojo en el listado para que repongas a tiempo.',
      },
    ],
  },

  expenses: {
    title: '¿Qué son los Gastos?',
    icon: 'Receipt',
    intro: 'Todo lo que pagas para tu negocio: alquiler, materiales, comidas con clientes, software, formación, etc. Se registran aquí para que sean <strong>deducibles fiscalmente</strong> y para que aparezcan en cash flow y predicción de impuestos.',
    sections: [
      {
        icon: 'Plus', iconColor: '#FF6B4A', title: 'Cómo registrar gastos',
        body: [
          '<strong>Manual</strong>: pulsa "Nuevo gasto" e introduce los datos',
          '<strong>Foto del ticket</strong>: súbelo a la Bandeja OCR y la IA lo lee solo',
          '<strong>Importar masivo</strong>: desde Excel',
          'Cada gasto asocia proveedor + categoría + IVA + base imponible',
        ],
      },
      {
        icon: 'Sparkles', iconColor: '#7C3AED', title: 'Deducibilidad',
        body: [
          'Marca cada gasto como <strong>deducible o no</strong> según norma',
          'IVA deducible se acumula al modelo 303',
          'Base deducible reduce tu base IRPF (modelo 130)',
          'Combustible, comidas y dietas tienen reglas especiales (la app las aplica)',
        ],
      },
      {
        icon: 'AlertTriangle', iconColor: '#F59E0B', title: 'Importante',
        body: 'Conserva los <strong>justificantes</strong> (ticket, factura). Hacienda los puede pedir hasta 4 años después. La app permite adjuntar la foto del original a cada gasto.',
      },
    ],
  },

  'expenses-inbox': {
    title: '¿Qué es la Bandeja OCR?',
    icon: 'Upload',
    intro: 'Una <strong>bandeja inteligente</strong> donde subes fotos o PDFs de tickets, facturas de compra y recibos. La IA los <strong>lee automáticamente</strong> y crea el gasto con todos los datos (proveedor, importe, IVA, fecha, categoría).',
    sections: [
      {
        icon: 'Upload', iconColor: '#FF6B4A', title: 'Cómo funciona',
        body: [
          '1. Subes <strong>fotos, PDFs o emails</strong> con el justificante',
          '2. La IA extrae proveedor, fecha, base imponible, IVA, total',
          '3. Te lo muestra para que confirmes o corrijas',
          '4. Al aprobarlo, se convierte en un gasto registrado',
        ],
      },
      {
        icon: 'Sparkles', iconColor: '#7C3AED', title: 'Trucos para mejor lectura',
        body: [
          'Foto <strong>bien iluminada y enfocada</strong>',
          'Ticket completo en el encuadre (no recortado)',
          'Si tienes el PDF original mejor que la foto',
          'Multi-página soportado (factura de 3 hojas se procesa entera)',
        ],
      },
      {
        icon: 'CheckCircle2', iconColor: '#10B981', title: 'Acepta o corrige',
        body: 'La IA acierta el 90-95% pero a veces el ticket está borroso. Antes de confirmar, revisa los datos. Si la IA falla mucho en un proveedor concreto, "ensénale" corrigiendo y la próxima vez ese proveedor se lee mejor.',
      },
    ],
  },

  calendar: {
    title: '¿Qué es el Calendario?',
    icon: 'CalendarDays',
    intro: 'Tu agenda integrada con la facturación. Marca <strong>vencimientos de facturas, reuniones con clientes, fechas de impuestos, eventos personalizados</strong> y lo que se te ocurra. Todo en una vista unificada.',
    sections: [
      {
        icon: 'Calendar', iconColor: '#FF6B4A', title: 'Qué aparece automáticamente',
        body: [
          'Vencimiento de facturas emitidas (cobrar)',
          'Vencimiento de facturas de gasto (pagar)',
          'Fechas de impuestos (303, 130, 111, 115, 347...)',
          'Suscripciones recurrentes (cuándo se generará la próxima)',
        ],
      },
      {
        icon: 'Plus', iconColor: '#7C3AED', title: 'Eventos personalizados',
        body: 'Añade reuniones, llamadas, deadlines de proyectos, tareas. Color personalizable. Recordatorios por email opcional.',
      },
    ],
  },

  projects: {
    title: '¿Qué son los Proyectos?',
    icon: 'FolderKanban',
    intro: 'Agrupan facturas, presupuestos, gastos y <strong>horas trabajadas</strong> bajo un mismo paraguas para saber cuánto te dejó (o costó) un trabajo concreto. Imprescindible si trabajas por proyectos en vez de por cuota fija.',
    sections: [
      {
        icon: 'TrendingUp', iconColor: '#FF6B4A', title: 'Rentabilidad real',
        body: [
          'Ves <strong>ingresos totales</strong> facturados del proyecto',
          '<strong>Gastos directos</strong> asociados (materiales, subcontratas)',
          '<strong>Horas trabajadas</strong> × tarifa = coste de tu tiempo',
          'Margen = ingresos - gastos - coste tiempo',
          'Identifica proyectos rentables vs ruinosos',
        ],
      },
      {
        icon: 'Clock', iconColor: '#7C3AED', title: 'Time tracking',
        body: 'Inicias un cronómetro mientras trabajas o registras horas a mano al final del día. Cada entrada se asigna a un proyecto. Útil para facturar por horas o solo para saber dónde se va tu tiempo.',
      },
      {
        icon: 'AlertTriangle', iconColor: '#F59E0B', title: 'Alertas de pérdidas',
        body: 'Si un proyecto está dando margen negativo (gastas más de lo que vas a cobrar), aparece marcado en rojo y te avisa en el dashboard. Para cortar a tiempo.',
      },
    ],
  },

  banking: {
    title: '¿Qué es la Banca?',
    icon: 'Landmark',
    intro: 'Tu <strong>cuaderno bancario manual</strong>. Sirve para registrar movimientos cuando no tienes activado PSD2, o para cuentas que no soportan Open Banking (algunas neobancos extranjeros, PayPal, etc.).',
    sections: [
      {
        icon: 'Plus', iconColor: '#FF6B4A', title: 'Cómo funciona',
        body: [
          'Creas tus cuentas (corriente, ahorro, PayPal...)',
          'Importas un extracto en <strong>CSV o Excel</strong> del banco',
          'Categorías y casas movimientos con facturas automáticamente',
          'Concilias para asegurarte de que todo cuadra',
        ],
      },
      {
        icon: 'Sparkles', iconColor: '#7C3AED', title: 'Diferencia con Banca PSD2',
        body: '<strong>Banca PSD2</strong> conecta directamente con tu banco y descarga movimientos cada día. <strong>Banca manual</strong> requiere subir tú el extracto. Si tu banco está en el listado PSD2, usa esa.',
      },
    ],
  },

  tax: {
    title: '¿Qué es el módulo Impuestos?',
    icon: 'Calculator',
    intro: 'Calcula y prepara tus <strong>modelos fiscales españoles</strong> a partir de tus facturas e ingresos: 303 (IVA), 130 (IRPF autónomos), 111 (retenciones profesionales), 115 (retenciones alquileres), 347 (operaciones con terceros), 349 (intracomunitarias), modelo 200 (sociedades).',
    sections: [
      {
        icon: 'FileText', iconColor: '#FF6B4A', title: 'Modelos disponibles',
        body: [
          '<strong>303</strong>: IVA trimestral (T1, T2, T3, T4)',
          '<strong>130</strong>: Pago fraccionado IRPF autónomos',
          '<strong>111</strong>: Retenciones a profesionales y trabajadores',
          '<strong>115</strong>: Retenciones de alquileres',
          '<strong>347</strong>: Operaciones con terceros >3.005,06 € anuales',
          '<strong>180</strong>: Resumen anual retenciones alquileres',
          '<strong>390</strong>: Resumen anual IVA',
        ],
      },
      {
        icon: 'CheckCircle2', iconColor: '#10B981', title: 'Qué hace por ti',
        body: [
          'Suma automática de bases imponibles e IVA',
          'Aplica exenciones (UE, exportaciones, ISP)',
          'Genera el <strong>borrador prerellenado</strong> para AEAT',
          'Avisa de plazos de presentación',
          'Histórico de declaraciones anteriores',
        ],
      },
      {
        icon: 'AlertTriangle', iconColor: '#F59E0B', title: 'Diferencia con Predicción Fiscal',
        body: '<strong>Impuestos</strong> calcula lo real con tus facturas YA emitidas. <strong>Predicción Fiscal</strong> estima lo que vas a pagar al cierre del trimestre/año basándose en tu ritmo actual.',
      },
    ],
  },

  reports: {
    title: '¿Qué son los Informes?',
    icon: 'BarChart3',
    intro: 'Análisis visual de tu negocio: <strong>ventas, gastos, rentabilidad, top clientes, evolución mensual, comparativas año a año</strong>. Útil para entender qué funciona y qué no, y para preparar reuniones con tu asesor/banco/socios.',
    sections: [
      {
        icon: 'BarChart3', iconColor: '#FF6B4A', title: 'Informes principales',
        body: [
          '<strong>Resumen P&L</strong>: pérdidas y ganancias',
          '<strong>Evolución mensual</strong>: ingresos vs gastos',
          '<strong>Top clientes</strong>: quién te aporta más',
          '<strong>Top servicios/artículos</strong>: qué se vende más',
          '<strong>Comparativa año a año</strong>: creciendo o decreciendo',
        ],
      },
      {
        icon: 'Sparkles', iconColor: '#7C3AED', title: 'Exportar',
        body: 'Todos los informes se exportan a Excel o PDF para llevarlos al asesor, banco o reuniones con socios.',
      },
    ],
  },

  history: {
    title: '¿Qué es el Historial?',
    icon: 'Calendar',
    intro: 'Vista cronológica de <strong>todo lo que ha pasado en tu cuenta</strong>: facturas emitidas, gastos registrados, pagos cobrados, suscripciones generadas, contratos firmados, cambios fiscales. Útil para auditar y rastrear.',
    sections: [
      {
        icon: 'Clock', iconColor: '#FF6B4A', title: 'Filtros disponibles',
        body: 'Filtra por tipo (factura, gasto, contrato...), por rango de fechas, por cliente o proveedor, o por usuario que hizo la acción (útil si trabajáis en equipo).',
      },
    ],
  },

  import: {
    title: '¿Qué es la Importación masiva?',
    icon: 'Upload',
    intro: 'Sube datos masivamente desde <strong>Excel o CSV</strong>: clientes, proveedores, servicios, artículos, gastos, facturas, etc. Ideal cuando migras desde otra herramienta (Excel, ContaPlus, etc.) o tienes un listado largo que añadir.',
    sections: [
      {
        icon: 'Upload', iconColor: '#FF6B4A', title: 'Cómo funciona',
        body: [
          '1. Eliges <strong>qué tipo de entidad</strong> vas a importar',
          '2. Descargas la <strong>plantilla Excel</strong> con los campos correctos',
          '3. Rellenas tus datos en la plantilla',
          '4. Subes el archivo, el sistema lo <strong>analiza y mapea</strong>',
          '5. Revisas el preview y confirmas',
        ],
      },
      {
        icon: 'Sparkles', iconColor: '#7C3AED', title: 'Inteligencia',
        body: [
          'Auto-detecta columnas aunque no se llamen igual ("Nombre", "Cliente", "Razón Social"...)',
          'Avisa de duplicados antes de importar',
          'Reporta errores línea a línea (importa lo correcto, descarta lo malo)',
          'Procesa archivos grandes en <strong>segundo plano</strong> sin bloquearte',
        ],
      },
    ],
  },

  templates: {
    title: '¿Qué son las Plantillas de factura?',
    icon: 'Palette',
    intro: 'Personalizan <strong>cómo se ve el PDF</strong> que envías al cliente y guardan <strong>contenido reutilizable</strong> para crear facturas en 1 clic.',
    sections: [
      {
        icon: 'Palette', iconColor: '#FF6B4A', title: 'Plantillas de diseño PDF',
        body: [
          '6 diseños profesionales <strong>preinstalados</strong> (Clásica, Moderna, Minimalista, Profesional, Serif Elegante, Técnica Mono)',
          'Puedes <strong>duplicarlas y personalizar</strong>: colores, fuente, logo, pie de página',
          'Una marcada como <strong>"por defecto"</strong> se usa en todas tus facturas',
          'Puedes asignar plantilla diferente <strong>por cliente</strong> o <strong>por factura</strong>',
        ],
      },
      {
        icon: 'FileText', iconColor: '#7C3AED', title: 'Plantillas de contenido',
        body: 'Guardas una factura "tipo" (cuota mensual desarrollo, mantenimiento informático, etc.) con sus conceptos, IVA, días de vencimiento y notas. Al crear nueva factura, la cargas con 1 clic y solo cambias el cliente.',
      },
    ],
  },

  help: {
    title: '¿Qué es Ayuda?',
    icon: 'HelpCircle',
    intro: 'Centro de soporte: <strong>guías paso a paso, FAQs y contacto</strong> con nuestro equipo. Aquí encuentras todo lo que necesitas para sacar partido a Facturcal.',
    sections: [
      {
        icon: 'FileText', iconColor: '#FF6B4A', title: 'Qué encontrarás',
        body: [
          'Guías por módulo',
          'Vídeos cortos de los flujos principales',
          'FAQ con las dudas más comunes',
          'Contacto directo con soporte',
        ],
      },
    ],
  },
};

export default HELP;
